// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
/**
 * Lecture d'un ordre de fabrication de clé de pose, sans React : statuts,
 * normalisation de la ligne rendue par l'API, expiration et souffrance
 * recalculées à l'affichage. Partagée par la liste des ordres et le dialogue de
 * préparation.
 */
import { dateTime, dateUtc } from '../../utils/dates';

// Un ordre émis resté sans /hello au-delà de ce délai est une pose qui n'a pas
// eu lieu (clé jamais gravée, serveur démarré sans réseau). Il ressort dans la
// liste : son secret n'étant plus lisible, il faudra en émettre un autre.
export const OVERDUE_HOURS = 24;

// Deux statuts en base (`app/models/ordre_cle.py`) : « expiré » se calcule à
// l'affichage, et il n'existe pas de « téléchargé », la remise du fichier étant
// la création elle-même, seul chemin par lequel le secret sort.
export const STATUS_DISPLAY = {
  emis: {
    label: 'À poser',
    couleur: 'info',
    aide: "Le fichier a été remis à la création : la clé est gravable. Le serveur n'a pas encore dit bonjour à l'API.",
  },
  pose_confirmee: {
    label: 'Pose confirmée',
    couleur: 'success',
    aide: 'Le serveur a dit bonjour à l\'API : l\'ordre s\'est refermé tout seul.',
  },
  expire: {
    label: 'Expiré',
    couleur: 'error',
    aide: "Passé 48 h l'ordre est expiré. Le serveur peut encore se poser jusqu'à la date indiquée sous l'échéance, puis son identité devient caduque. Une clé perdue s'annule ; pour poser, il faut en émettre une autre.",
  },
};

// Chasse fixe pour les chemins de fichier, à recopier tels quels.
export const CODE = {
  fontFamily: 'monospace',
  fontSize: '0.8rem',
  bgcolor: 'rgba(0,0,0,.06)',
  px: 0.6,
  py: 0.2,
  borderRadius: 0.5,
  whiteSpace: 'nowrap',
};

export function hoursSince(iso) {
  const d = dateUtc(iso);
  return d ? (Date.now() - d.getTime()) / 3600000 : null;
}

export function humanReadableDelay(hours) {
  if (hours === null) return '';
  const abs = Math.abs(hours);
  if (abs < 1) return `${Math.round(abs * 60)} min`;
  if (abs < 48) return `${Math.round(abs)} h`;
  return `${Math.round(abs / 24)} j`;
}

/** Une ligne d'ordre, telle que l'écran la lit. Les noms de champs du document
 *  de fabrication sont repris tels quels, avec deux tolérances : `ordre_id`
 *  dans le document contre `id` dans la liste, et un site rendu à plat plutôt
 *  qu'imbriqué. */
export function normalizeOrder(o) {
  const site = o.site && typeof o.site === 'object' ? o.site : null;
  return {
    id: o.id || o.ordre_id,
    statut: o.statut || o.status || 'emis',
    serie: o.serie || '',
    siteId: site?.id || o.site_id || '',
    siteNom: site?.nom || o.site_nom || '',
    organisation: site?.organisation || o.organisation || '',
    kitCode: o.kit?.code || o.kit_code || '',
    emisLe: o.emis_le || null,
    emisPar: o.emis_par || '',
    expireLe: o.expire_le || null,
    telechargeLe: o.telecharge_le || null,
    confirmeLe: o.pose_confirmee_le || o.confirme_le || null,
    chambres: o.chambres_attendues ?? null,
    // Rendus par la liste (cles_de_pose._ordre_dict). Absents : null, jamais
    // une valeur devinée ici.
    poseJusquAu: o.pose_possible_jusqu_au || null,
    identiteCaduque: typeof o.identite_caduque === 'boolean' ? o.identite_caduque : null,
    // Repère de pose (bâtiment, aile, étage) : il distingue deux serveurs du
    // même établissement, qui sans lui ne se départagent que par la série.
    repere: o.notes || '',
  };
}

/** L'expiration est une date, pas un état stocké : un ordre émis il y a 49 h
 *  est expiré même si le serveur ne l'a pas encore repassé en base. Elle se
 *  recalcule à l'affichage, sauf pour une pose confirmée, qui ne s'annule pas
 *  parce que l'ordre a vieilli. */
export function effectiveStatus(o) {
  if (o.statut === 'pose_confirmee') return 'pose_confirmee';
  // `hoursSince(expire_le)` est positif quand l'échéance est passée.
  const exceeded = o.expireLe ? hoursSince(o.expireLe) : null;
  if (exceeded !== null && exceeded > 0) return 'expire';
  return STATUS_DISPLAY[o.statut] ? o.statut : 'emis';
}

/** Ce que la colonne d'échéance ajoute sur l'identité du serveur, ou null.
 *  Le backend calcule les deux champs (même règle que `provision._auth_kit`) :
 *  l'écran les lit sans refaire le calcul. Tant que le délai de pose court,
 *  une clé expirée et perdue poserait encore un serveur : la ligne le dit. */
export function identityState(o, statusValue) {
  if (statusValue === 'pose_confirmee') return null;
  if (o.identiteCaduque === true) {
    return {
      texte: 'identité caduque : ce serveur ne peut plus se poser',
      aide: "Délai de pose dépassé sans que le serveur ait dit bonjour : l'API refuse désormais son identité. Pour poser, préparer une autre clé.",
      couleur: 'error.main',
    };
  }
  if (statusValue === 'expire' && o.poseJusquAu) {
    return {
      texte: `pose encore acceptée jusqu'au ${dateTime(o.poseJusquAu, '—')}`,
      aide: "Une clé perdue poserait encore un serveur jusqu'à cette date : l'annuler si elle ne doit plus servir.",
      couleur: 'warning.main',
    };
  }
  return null;
}

export function isOverdue(o) {
  if (effectiveStatus(o) !== 'emis') return false;
  const age = hoursSince(o.emisLe);
  return age !== null && age >= OVERDUE_HOURS;
}
